import { c as _c } from "react/compiler-runtime";
import { create, useStore } from 'zustand';
import { persist } from 'zustand/middleware';
import { useShallow } from 'zustand/shallow';
import { useEffect } from 'react';
import type { MeasuredKeyRequirements, OptionalNumber } from '@/lib/piano/touch-design/measured-key.requirements';
import { useKeyboard } from '../keyboard/use-keyboard';
import type { KeyboardRequirements } from '@/lib/piano/keyboard';
export interface MeasuresStoreState {
  keys: MeasuredKeyRequirements[];
  keyWeightRatio: OptionalNumber;
  wippenRadiusWeight: OptionalNumber;
}
interface VersionedMeasuresStoreState extends MeasuresStoreState {
  version: number;
}
type GlobalMeasures = Pick<MeasuresStoreState, 'keyWeightRatio' | 'wippenRadiusWeight'>;
interface MeasuresStoreActions {
  reset: () => void;
  updateKeyMeasure: (keyIndex: number, property: keyof MeasuredKeyRequirements, value: OptionalNumber) => void;
  updateKeyMeasures: (keyIndex: number, keySpec: MeasuredKeyRequirements) => void;
  updateGlobalMeasure: (property: keyof GlobalMeasures, value: OptionalNumber) => void;
  updateState: (newState: MeasuresStoreState) => void;
  syncKeyboardSize: (targetSize: number) => void;
}
export type MeasuresStore = VersionedMeasuresStoreState & MeasuresStoreActions;
const defaultKeySpec: MeasuredKeyRequirements = {
  downWeightWithSpringSupport: null,
  downWeightWithoutSpringSupport: null,
  frontWeight: null,
  keyWeightRatio: null,
  measuredStrikeWeightRatio: null,
  strikeWeight: null,
  upWeight: null,
  wippenRadiusWeight: null
};
const createDefaultMeasuresStoreValues = (keyboardSize: number): MeasuresStoreState => ({
  keyWeightRatio: null,
  keys: Array.from({
    length: keyboardSize
  }, () => ({
    ...defaultKeySpec
  })),
  wippenRadiusWeight: null
});
const createMeasuresStore = (keyboard: KeyboardRequirements) => create<MeasuresStore>()(persist(set => ({
  ...createDefaultMeasuresStoreValues(keyboard.size),
  reset: () => set(() => createDefaultMeasuresStoreValues(keyboard.size)),
  syncKeyboardSize: (targetSize: number) => set(state => {
    if (state.keys.length < targetSize) {
      const newKeys = [...state.keys];
      for (let i = state.keys.length; i < targetSize; i++) {
        newKeys.push({
          ...defaultKeySpec
        });
      }
      return {
        keys: newKeys
      };
    }
    return state;
  }),
  updateGlobalMeasure: (property, value) => set(() => ({
    [property]: value
  })),
  updateKeyMeasure: (keyIndex, property, value) => set(state => ({
    keys: state.keys.map((spec, index) => index === keyIndex ? {
      ...spec,
      [property]: value
    } : spec)
  })),
  updateKeyMeasures: (keyIndex, keySpec) => set(state => ({
    keys: state.keys.map((spec, index) => index === keyIndex ? keySpec : spec)
  })),
  updateState: (newState: MeasuresStoreState) => set(newState),
  version: 1,
  wippenRadiusWeight: null
}), {
  name: `ptl.measures`,
  partialize: (state: MeasuresStore): VersionedMeasuresStoreState => ({
    keyWeightRatio: state.keyWeightRatio,
    keys: state.keys,
    version: state.version,
    wippenRadiusWeight: state.wippenRadiusWeight
  })
}));
type MeasuresBoundStore = ReturnType<typeof createMeasuresStore>;
let measuresStore: MeasuresBoundStore | undefined = undefined;
export const useMeasuresStore = (): MeasuresBoundStore => {
  const {
    keyboard
  } = useKeyboard();
  measuresStore ??= createMeasuresStore(keyboard);
  useEffect(() => {
    measuresStore?.getState().syncKeyboardSize(keyboard.size);
  }, [keyboard.size]);
  return measuresStore;
};
export const useMeasuredKeyFromStore = keyIndex => {
  const $ = _c(2);
  let t0;
  if ($[0] !== keyIndex) {
    t0 = state => state.keys?.[keyIndex] ?? defaultKeySpec;
    $[0] = keyIndex;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useStore(useMeasuresStore(), useShallow(t0));
};
export const useGlobalMeasures = () => {
  return useStore(useMeasuresStore(), useShallow(_temp));
};
export const useMeasureActions = () => {
  return useStore(useMeasuresStore(), useShallow(_temp2));
};
function _temp(state) {
  return {
    keyWeightRatio: state.keyWeightRatio,
    wippenRadiusWeight: state.wippenRadiusWeight
  };
}
function _temp2(state) {
  return {
    reset: state.reset,
    updateGlobalMeasure: state.updateGlobalMeasure,
    updateKeyMeasure: state.updateKeyMeasure,
    updateKeyMeasures: state.updateKeyMeasures,
    updateState: state.updateState
  };
}